import { cast, getSnapshot, Instance, types } from 'mobx-state-tree';
import { IVI, VI } from './VI';
import { SupportedMapLayers } from './enums';
import { FeatureStore } from './FeatureStore';
import { SoilSample } from './SoilSample';
import { dateSortValidation, isPresent } from '@farmai-shared/LFUtils';
import * as turf from '@turf/turf';
import { basicSort } from '@services/utils';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { checkPointWithinCircle } from '@app/_components/lf-map/utils';
type LFMapHints = 'ruler' | 'cropzone' | 'drawBoundary';
type LayerInfoSheetMessages = 'no_hotspots_detected';
const isNewBboxWithinPrevious = (newBbox: number[][], oldBbox: number[][]) => {
  if (!isPresent(oldBbox) || newBbox.length !== 2 || oldBbox.length !== 2) return false;
  const flatNewBbox = newBbox.flat();
  const flatOldBbox = oldBbox.flat();
  if (flatNewBbox.length !== 4 || flatOldBbox.length !== 4) {
    return false;
  }
  return turf.booleanWithin(turf.bboxPolygon(flatNewBbox as [number, number, number, number]), turf.bboxPolygon(flatOldBbox as [number, number, number, number]));
};
interface FeatureSnapshot {
  geometry: {
    coordinates: number[];
  };
  properties: Record<string, any>;
}
export const SelectedMarkerFeature = types.custom<FeatureSnapshot, Feature<Point> | null>({
  name: 'SelectedMarkerFeature',
  // Convert a snapshot to an instance.
  fromSnapshot(snapshot) {
    if (!isPresent(snapshot)) return null;
    const geometry = new Point(snapshot.geometry.coordinates);
    // Remove the geometry key from properties to avoid duplication.
    const {
      geometry: _ignored,
      ...props
    } = snapshot.properties || {};
    const feature = new Feature({
      geometry,
      ...props
    });
    return feature;
  },
  // Convert a runtime Feature<Point> instance into a snapshot.
  toSnapshot(feature: Feature<Point>): FeatureSnapshot {
    const geometry = feature.getGeometry() as Point;
    return {
      geometry: {
        coordinates: geometry.getCoordinates()
      },
      properties: feature.getProperties()
    };
  },
  isTargetType(value): boolean {
    return value instanceof Feature && value.getGeometry() instanceof Point;
  },
  getValidationMessage(snapshot): string {
    if (typeof snapshot !== 'object' || !isPresent(snapshot) || !snapshot.geometry || !Array.isArray(snapshot.geometry.coordinates)) {
      return 'Invalid snapshot for Feature<Point>: expected an object with a "geometry" property that has a "coordinates" array.';
    }
    return '';
  }
});
const LayerInfoSheet = types.model({
  layer: types.maybeNull(types.frozen<SupportedMapLayers>()),
  // should only really ne `NONE` or null
  message: types.maybeNull(types.frozen<LayerInfoSheetMessages>())
}).actions(self => ({
  setLayer(layer: SupportedMapLayers | null) {
    self.layer = layer;
  },
  setMessage(message: LayerInfoSheetMessages | null) {
    self.message = message;
  },
  reset() {
    self.layer = null;
    self.message = null;
  }
}));
export const LFMap = types.model({
  location: types.array(types.number),
  permissionStatus: types.maybeNull(types.string),
  bbox: types.array(types.array(types.number)),
  socketBbox: types.array(types.array(types.number)),
  // used to store only the 'novel' bbox outside of previous bboxes
  rulerEnabled: false,
  hint: types.maybeNull(types.frozen<LFMapHints>()),
  vis: types.array(VI),
  soilSamples: types.map(SoilSample),
  featureStore: types.optional(FeatureStore, () => FeatureStore.create()),
  isDrawingFeature: false,
  isEditing: false,
  selectedMarker: types.maybeNull(SelectedMarkerFeature),
  centreMarker: types.model({
    isOnBoundary: false,
    isOnAnyPoint: false,
    isOnFirstPoint: false
  }).views(self => ({
    get canAddPoint() {
      return self.isOnBoundary && !self.isOnAnyPoint;
    }
  })),
  isMovingState: false,
  isMapVisibleMobile: false,
  layerInfoSheet: LayerInfoSheet,
  isInNavigationMode: false,
  isMobileMapSearchActive: false
}).actions(self => ({
  setLocation(location: number[], fromSearch = false) {
    self.location.replace(location);
    self.isMobileMapSearchActive = fromSearch;
  },
  setPermissionStatus(status: string) {
    self.permissionStatus = status;
  },
  setBbox(bbox: number[][]) {
    self.bbox = cast(bbox);
  },
  setSocketBbox(bbox: number[][]) {
    if (isNewBboxWithinPrevious(bbox, self.socketBbox)) return;
    self.socketBbox = cast(bbox);
  },
  setIsMapVisibleMobile(isVisible: boolean) {
    self.isMapVisibleMobile = isVisible;
  },
  resetBboxes() {
    self.socketBbox.clear();
    self.bbox.clear();
  },
  copyBboxToSocketBbox() {
    self.socketBbox = cast(getSnapshot(self.bbox));
  },
  toggleRuler() {
    self.rulerEnabled = !self.rulerEnabled;
  },
  setRuler(isEnabled: boolean) {
    self.rulerEnabled = isEnabled;
  },
  setHint(hint: LFMapHints | null) {
    self.hint = hint;
  },
  pushToVis(vi: IVI) {
    self.vis.push(vi);
  },
  replaceVis(vis: IVI[]) {
    self.vis.replace(vis);
    return self.vis.sort((a, b) => {
      const {
        first,
        second
      } = dateSortValidation(a.datetimeZ, b.datetimeZ);
      return basicSort(first, second, 'asc');
    });
  },
  addSamples(samples) {
    return samples.map(s => self.soilSamples.put(s));
  },
  setDrawingFeature(isDrawing: boolean) {
    self.isDrawingFeature = isDrawing;
  },
  setIsEditing(isEditing: boolean) {
    self.isEditing = isEditing;
  },
  updateCentreMarkerStatus(center: number[], resolution: number | undefined) {
    const polygon = self.featureStore.selectedFeature?.getPolygon;
    const coordinates = polygon?.outerCoords;
    if (isPresent(coordinates) && coordinates.length > 2 && resolution) {
      const firstPoint = coordinates[0];
      const isOnBoundary = turf.booleanPointOnLine(turf.point(center), turf.lineString(coordinates), {
        epsilon: 1e-7
      });
      self.centreMarker.isOnBoundary = isOnBoundary;
      const isOnFirstPoint = checkPointWithinCircle(center, firstPoint, resolution);
      if (isOnFirstPoint) {
        self.centreMarker.isOnFirstPoint = isOnFirstPoint && !self.isEditing;
        self.centreMarker.isOnAnyPoint = true;
        return;
      }
      const isOnAnyPoint = coordinates.some(coord => checkPointWithinCircle(center, coord, resolution));
      self.centreMarker.isOnAnyPoint = isOnAnyPoint;
      return;
    }
    self.centreMarker.isOnBoundary = false;
    self.centreMarker.isOnFirstPoint = false;
  },
  setIsMovingState(isMoving: boolean) {
    self.isMovingState = isMoving;
  },
  setSelectedMarker(marker: Feature<Point> | null) {
    self.selectedMarker = marker;
  },
  setNavigationMode(isNavigating: boolean) {
    self.isInNavigationMode = isNavigating;
  },
  setIsMobileMapSearchActive(isActive: boolean) {
    self.isMobileMapSearchActive = isActive;
  },
  reset() {
    self.isDrawingFeature = false;
    self.isEditing = false;
    self.centreMarker = cast({
      isOnAnyPoint: false,
      isOnBoundary: false,
      isOnFirstPoint: false
    });
    self.selectedMarker = null;
    self.isMovingState = false;
    self.isMapVisibleMobile = false;
    self.layerInfoSheet.reset();
    self.featureStore.reset();
    self.isInNavigationMode = false;
    self.isMobileMapSearchActive = false;
  }
})).views(self => ({
  get hasPermissionAndLocation() {
    return self.location.length === 2 && self.permissionStatus !== 'denied';
  },
  get placesLocation() {
    return [self.location[0], self.location[1]];
  },
  get hasBbox() {
    return self.bbox.flat().length > 1 && !self.bbox.flat().includes(NaN);
  },
  get extent() {
    if (this.hasBbox) {
      return [self.bbox[0][0], self.bbox[1][1], self.bbox[1][0], self.bbox[0][1]];
    }
  },
  get viIds() {
    return self.vis.reduce((acc, n) => {
      acc[n.id] = true;
      return acc;
    }, {});
  },
  get socketBboxFlatString() {
    return self.socketBbox.flat().toString();
  },
  get isUndoDisabled() {
    if (isPresent(self.featureStore.selectedFeature?.getPolygon?.outerCoords)) {
      return self.featureStore.selectedFeature.getPolygon.outerCoords.length === 0;
    }
    return true;
  },
  get isRemovalDisabled() {
    if (isPresent(self.featureStore.selectedFeature?.getPolygon?.outerCoords)) {
      return self.featureStore.selectedFeature.getPolygon.outerCoords?.length <= 4; // account for closure
    }
    return true;
  },
  get isCompleteDisabled() {
    if (isPresent(self.featureStore.selectedFeature?.getPolygon?.outerCoords)) {
      return self.featureStore.selectedFeature.getPolygon.outerCoords.length < 3;
    }
    return true;
  }
}));
export type IMapStore = Instance<typeof LFMap>;